import { toWei } from 'web3-utils';
import React from 'react';
import modalify from '@blockcatio/tabby-library/lib/utilities/modalify';
import { walletDefinitions } from '@blockcatio/tabby-library/lib/utilities/walletTypes';
import WalletSelectionContainer from '@blockcatio/tabby-library/lib/components/wallet/WalletSelectionContainer';

import {
  ACCOUNT_WALLET_RESET,
  ACCOUNT_WALLET_SELECT,
  ACCOUNT_NOT_PROVIDED,
  ACCOUNT_CHANGED,
  ACCOUNT_CHANGE_FAILED,
  ACCOUNT_BALANCE_UPDATED,
  ACCOUNT_BALANCE_UPDATE_FAILED,
  ACCOUNT_NETWORK_CHANGED,
  ACCOUNT_NETWORK_CHANGE_FAILED,
  CAT_BALANCE_UPDATED,
  CAT_BALANCE_UPDATE_FAILED,
  CAT_ALLOWANCE_UPDATED,
  CAT_ALLOWANCE_UPDATE_FAILED,
  CAT_AUTHORIZATION_STARTED,
  CAT_AUTHORIZATION_SUBMITTED,
  CAT_AUTHORIZATION_FAILED,
  CAT_AUTHORIZATION_ABORTED,
  CAT_AUTHORIZATION_CONFIRMED,
  CAT_AUTHORIZATION_REJECTED,
  ACCOUNT_REFRESHING,
  ACCOUNT_WALLET_RELOAD_STARTED,
  ACCOUNT_WALLET_RELOAD_FAILED,
  ACCOUNT_WALLET_RELOAD_COMPLETED,
} from '../actionTypes';

export const accountChange = address => ({
  type: ACCOUNT_CHANGED,
  address,
});

export const accountChangeFail = error => ({
  type: ACCOUNT_CHANGE_FAILED,
  error,
});

export const accountNotProvided = () => ({
  type: ACCOUNT_NOT_PROVIDED,
});

export const accountWalletSelect = (walletType, walletOptions) => ({
  type: ACCOUNT_WALLET_SELECT,
  walletType,
  walletOptions,
});

export const accountWalletReset = () => ({
  type: ACCOUNT_WALLET_RESET,
});

export const accountRefresh = () => ({ type: ACCOUNT_REFRESHING });

export const accountWalletReloadStarted = () => ({ type: ACCOUNT_WALLET_RELOAD_STARTED });

export const accountWalletReloadFailed = error => ({ type: ACCOUNT_WALLET_RELOAD_FAILED, error });

export const accountWalletReloadCompleted = () => ({ type: ACCOUNT_WALLET_RELOAD_COMPLETED });

/**
 * Attempt to reload a persisted wallet type and options.
 * If the wallet is supported by the browser (approved/can communicate with it, etc),
 * set the type and options, if not, leave things in a no wallet selected state.
 *
 * @param {String} walletType
 * @param {Object} walletOptions
 */
export const accountWalletReload = (walletType, walletOptions) => async (
  dispatch,
  getState,
  { walletManager, givenProvider },
) => {
  dispatch(accountWalletReloadStarted());

  const walletDefinition = walletDefinitions.find(definition => definition.type === walletType);
  if (walletDefinition) {
    try {
      const isSupported = await walletManager.isTypeSupported(walletType, givenProvider);
      if (isSupported) {
        dispatch(accountWalletSelect(walletType, walletOptions));
      }
    } catch (error) {
      dispatch(accountWalletReloadFailed(error));
    }
  }
  dispatch(accountWalletReloadCompleted());
};

export const accountWalletChange = () => async (
  dispatch,
  getState,
  { walletManager, givenProvider },
) => {
  // eslint-disable-next-line react/prop-types
  const CloseWrapper = ({ close, ...props }) => {
    const onWalletSelect = (walletType, walletOptions) => {
      close({ walletType, walletOptions });
    };
    const { supportedWallets } = getState().app;
    const supportedWalletDefinitions = walletDefinitions.filter(def =>
      supportedWallets.includes(def.type));
    return (
      <WalletSelectionContainer
        {...props}
        walletDefinitions={supportedWalletDefinitions}
        givenProvider={givenProvider}
        onWalletSelect={onWalletSelect}
        onWalletIsSupported={walletManager.isTypeSupported}
      />
    );
  };
  CloseWrapper.displayName = 'WalletSelectionContainer';

  const modal = modalify(CloseWrapper);
  const result = await modal({ walletDefinitions });
  if (result) {
    dispatch(accountWalletReset());
    dispatch(accountWalletSelect(result.walletType, result.walletOptions));
  }
};

export const accountNetworkChange = network => ({
  type: ACCOUNT_NETWORK_CHANGED,
  network,
});

export const accountNetworkChangeFail = network => ({
  type: ACCOUNT_NETWORK_CHANGE_FAILED,
  network,
});

export const accountBalanceUpdate = (address, balance) => ({
  type: ACCOUNT_BALANCE_UPDATED,
  address,
  balance,
});

export const accountBalanceUpdateFail = (address, error) => ({
  type: ACCOUNT_BALANCE_UPDATE_FAILED,
  address,
  error,
});

export const catAllowanceUpdate = catAllowance => ({
  type: CAT_ALLOWANCE_UPDATED,
  catAllowance,
});

export const catAllowanceUpdateFailed = error => ({
  type: CAT_ALLOWANCE_UPDATE_FAILED,
  error,
});

export const catBalanceUpdate = catBalance => ({
  type: CAT_BALANCE_UPDATED,
  catBalance,
});

export const catBalanceUpdateFailed = error => ({
  type: CAT_BALANCE_UPDATE_FAILED,
  error,
});

export const authorizeCatTokenStart = () => ({
  type: CAT_AUTHORIZATION_STARTED,
});

export const authorizeCatTokenSubmitted = transactionHash => ({
  type: CAT_AUTHORIZATION_SUBMITTED,
  transactionHash,
});

export const authorizeCatTokenAborted = () => ({
  type: CAT_AUTHORIZATION_ABORTED,
});

export const authorizeCatTokenFailed = error => ({
  type: CAT_AUTHORIZATION_FAILED,
  error,
});

export const authorizeCat = amount => async (
  dispatch,
  getState,
  { catToken, ethGateway, errorReporter },
) => {
  const {
    account: { address },
  } = getState();

  const gasPrice = toWei('2', 'gwei');

  try {
    return await ethGateway.process(getState, {
      create: () => catToken.authorizeCatTransaction(address, amount, gasPrice),
      onStart: () => dispatch(authorizeCatTokenStart()),
      onSuccess: transactionHash => dispatch(authorizeCatTokenSubmitted(transactionHash)),
      onError: error => dispatch(authorizeCatTokenFailed(error)),
      onAbort: () => dispatch(authorizeCatTokenAborted()),
    });
  } catch (error) {
    errorReporter(error);
    return null;
  }
};

export const authorizeCatConfirmed = transactionHash => ({
  type: CAT_AUTHORIZATION_CONFIRMED,
  transactionHash,
});

export const authorizeCatRejected = transactionHash => ({
  type: CAT_AUTHORIZATION_REJECTED,
  transactionHash,
});



// WEBPACK FOOTER //
// ./src/actions/account.js